import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { createComplaint, getComplaintRecipients } from "../../api/complaints";
import { requestInvestigation } from "../../api/investigations";
import { uploadAttachments } from "../../api/attachments";
import {
  useNotifications,
  createNotification,
  NOTIFICATION_TYPES,
} from "../../context/NotificationsContext";
import {
  isValidComplaintRecipient,
  canUserSendComplaint,
  COMPLAINT_STATUS,
  COMPLAINT_KIND,
  getComplaintKindLabel,
  notifyHrOfNewComplaint,
} from "../../utils/complaintHelpers";
import ComplaintCard from "../common/ComplaintCard";
import AttachmentUploader from "../common/AttachmentUploader";
import ResignRequestForm from "./ResignRequestForm";
import "./MyComplaints.css";
import "./ComplaintsRequestForm.css";
import "./ResignRequestForm.css";

export default function MyComplaints() {
  const { user } = useAuth();
  const { complaints, addComplaint, findRoleEmployee, addRecentActivity } =
    useData();
  const { addNotificationToUser } = useNotifications();

  const [showForm, setShowForm] = useState(false);
  const [showResign, setShowResign] = useState(false);
  const [investigationTarget, setInvestigationTarget] = useState(null);
  const [investigationNote, setInvestigationNote] = useState("");
  const [investigationError, setInvestigationError] = useState("");
  const [investigationSaving, setInvestigationSaving] = useState(false);
  const [investigationSent, setInvestigationSent] = useState(false);
  const [kind, setKind] = useState(COMPLAINT_KIND.COMPLAINT);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [receiverId, setReceiverId] = useState("");
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [successKind, setSuccessKind] = useState(COMPLAINT_KIND.COMPLAINT);
  const [recipients, setRecipients] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [recipientsError, setRecipientsError] = useState("");

  const kindLabel = getComplaintKindLabel(kind);
  const isInquiry = kind === COMPLAINT_KIND.INQUIRY;

  useEffect(() => {
    if (!canUserSendComplaint(user?.role)) return undefined;

    let cancelled = false;
    setRecipientsLoading(true);
    setRecipientsError("");

    getComplaintRecipients()
      .then((rows) => {
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : [];
        setRecipients(list);
      })
      .catch(() => {
        if (cancelled) return;
        setRecipients([]);
        setRecipientsError("تعذر تحميل المستلمين المتاحين");
      })
      .finally(() => {
        if (!cancelled) setRecipientsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.role, user?.id]);

  const myComplaints = useMemo(
    () =>
      [...complaints]
        .filter((c) => c.senderId === user?.id)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [complaints, user?.id],
  );

  if (!canUserSendComplaint(user?.role)) {
    return (
      <div className="my-complaints">
        <div className="ess-home-header">
          <h1 className="ess-home-title">شكاواي واستفساراتي</h1>
          <p className="ess-home-subtitle">
            لا يمكنك تقديم شكاوى أو استفسارات في هذا الدور
          </p>
        </div>
      </div>
    );
  }

  function resetForm() {
    setTitle("");
    setDescription("");
    setReceiverId("");
    setKind(COMPLAINT_KIND.COMPLAINT);
    setFiles([]);
    setError("");
    setSuccess(false);
    setShowForm(false);
    setShowResign(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (!title.trim()) {
      setError(`من فضلك أدخل عنوان ال${kindLabel}`);
      return;
    }
    if (!description.trim()) {
      setError(`من فضلك أدخل نص ال${kindLabel}`);
      return;
    }
    if (recipients.length === 0) {
      setError(
        recipientsError ||
          "لا يوجد مستلمون متاحون في سلسلتك الإدارية. تأكد من تعيين مدير مباشر أو تواصل مع الموارد البشرية.",
      );
      return;
    }
    if (!receiverId) {
      setError("من فضلك اختر المستلم من السلسلة الإدارية");
      return;
    }

    const receiver = recipients.find((r) => String(r.id) === String(receiverId));

    if (
      !receiver ||
      !isValidComplaintRecipient(
        user.role,
        receiver.role,
        user.backendRoles || [],
      )
    ) {
      setError(`لا يمكنك إرسال ${kindLabel} لهذا المستوى`);
      return;
    }

    try {
      const created = await createComplaint({
        title: title.trim(),
        description: description.trim(),
        kind,
        priority: "MEDIUM",
        targetRole: receiver.role,
      });

      if (files.length && created?.id) {
        await uploadAttachments("complaint", created.id, files);
        setFiles([]);
      }

      await addComplaint();

      const hrEmployee = findRoleEmployee("hr");
      const submittedKindLabel = getComplaintKindLabel(kind);

      if (receiver) {
        const notif = createNotification({
          recipientId: receiver.id,
          recipientRole: receiver.role,
          type: NOTIFICATION_TYPES.COMPLAINT_NEW,
          title: isInquiry ? "📩 استفسار جديد" : "📩 شكوى جديدة",
          message: `${submittedKindLabel} جديد${isInquiry ? "" : "ة"} من ${user.name}: ${title.trim()}`,
          relatedRequestId: null,
          senderName: user.name,
          senderRole: user.role,
          requestType: "complaint",
          requestStatus: COMPLAINT_STATUS.PENDING,
        });
        addNotificationToUser(receiver.id, notif);
      }

      notifyHrOfNewComplaint(
        {
          id: null,
          title: title.trim(),
          kind,
          senderName: user.name,
          senderRole: user.role,
          receiverId: receiver?.id,
          receiverName: receiver?.name,
          receiverRole: receiver?.role,
          status: COMPLAINT_STATUS.PENDING,
        },
        {
          createNotification,
          NOTIFICATION_TYPES,
          addNotificationToUser,
          hrEmployeeId: hrEmployee?.id,
        },
      );

      addRecentActivity({
        id: Date.now(),
        text: `${submittedKindLabel} جديد${isInquiry ? "" : "ة"} من ${user.name}${receiver ? ` إلى ${receiver.name}` : ""}`,
        date: new Date().toLocaleDateString("ar-EG"),
        type: "complaint",
      });

      setSuccessKind(kind);
      setSuccess(true);
      setShowForm(false);
    } catch (err) {
      const message = err.response?.data?.message;
      setError(
        Array.isArray(message)
          ? message.join("\n")
          : message || `تعذر إرسال ال${kindLabel}`,
      );
    }
  }

  const successLabel = getComplaintKindLabel(successKind);
  const successIsInquiry = successKind === COMPLAINT_KIND.INQUIRY;

  return (
    <div className="my-complaints" dir="rtl">
      <div className="ess-home-header">
        <h1 className="ess-home-title">الشكاوى والاستفسارات</h1>
        <p className="ess-home-subtitle">
          تقديم شكوى أو استفسار، أو طلب استقالة رسمي
        </p>
      </div>

      {!showForm && !showResign && !success && (
        <div className="my-complaints-actions">
          <button
            className="my-complaints-create-btn"
            onClick={() => {
              setShowForm(true);
              setShowResign(false);
              setKind(COMPLAINT_KIND.COMPLAINT);
              if (recipients.length === 1) {
                setReceiverId(String(recipients[0].id));
              }
            }}
          >
            + تقديم شكوى أو استفسار
          </button>
          <button
            className="my-complaints-resign-btn"
            onClick={() => {
              setShowResign(true);
              setShowForm(false);
            }}
          >
            طلب استقالة
          </button>
        </div>
      )}

      {showResign && (
        <div className="my-complaints-resign-section">
          <button
            type="button"
            className="complaint-secondary-btn my-complaints-back-btn"
            onClick={() => setShowResign(false)}
          >
            ← رجوع
          </button>
          <ResignRequestForm />
        </div>
      )}

      {success && (
        <div className="complaint-form-card">
          <div className="complaint-success-box">
            <p className="complaint-success-title">
              تم إرسال ال{successLabel} بنجاح
            </p>
            <p className="complaint-success-subtitle">
              تم إرسال{" "}
              {successIsInquiry ? "استفسارك" : "شكواك"} وسيتم إشعار المستلم.
              يمكنك متابعة الحالة من القائمة أدناه.
            </p>
            <button className="complaint-secondary-btn" onClick={resetForm}>
              إغلاق
            </button>
          </div>
        </div>
      )}

      {showForm && (
        <form className="complaint-form-card" onSubmit={handleSubmit}>
          <h3 className="my-complaints-form-title">تقديم شكوى أو استفسار</h3>

          <div className="complaint-field">
            <label className="complaint-label">النوع *</label>
            <div className="my-complaints-kind-list" role="radiogroup">
              <label
                className={`my-complaints-kind-option ${
                  kind === COMPLAINT_KIND.COMPLAINT ? "is-selected" : ""
                }`}
              >
                <input
                  type="radio"
                  name="complaintKind"
                  value={COMPLAINT_KIND.COMPLAINT}
                  checked={kind === COMPLAINT_KIND.COMPLAINT}
                  onChange={() => setKind(COMPLAINT_KIND.COMPLAINT)}
                />
                <span>شكوى</span>
              </label>
              <label
                className={`my-complaints-kind-option ${
                  kind === COMPLAINT_KIND.INQUIRY ? "is-selected" : ""
                }`}
              >
                <input
                  type="radio"
                  name="complaintKind"
                  value={COMPLAINT_KIND.INQUIRY}
                  checked={kind === COMPLAINT_KIND.INQUIRY}
                  onChange={() => setKind(COMPLAINT_KIND.INQUIRY)}
                />
                <span>استفسار</span>
              </label>
            </div>
          </div>

          <div className="complaint-field">
            <label className="complaint-label">المستلم *</label>
            <p className="my-complaints-hint">
              اختر المستوى الذي تريد إرسال ال{kindLabel} إليه من السلسلة
              الإدارية
            </p>
            <div className="my-complaints-recipient-list">
              {recipientsLoading && (
                <p className="my-complaints-hint">جاري تحميل المستلمين...</p>
              )}
              {!recipientsLoading && recipients.length === 0 && (
                <p className="complaint-error">
                  {recipientsError ||
                    "لا يوجد مستلمون متاحون حالياً. تأكد من تعيين مدير مباشر لك أو تواصل مع الموارد البشرية."}
                </p>
              )}
              {!recipientsLoading &&
                recipients.map((r, index) => (
                  <label
                    key={r.id}
                    className={`my-complaints-recipient-option ${
                      String(receiverId) === String(r.id) ? "is-selected" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="complaintRecipient"
                      value={r.id}
                      checked={String(receiverId) === String(r.id)}
                      onChange={(e) => setReceiverId(e.target.value)}
                    />
                    <div className="my-complaints-recipient-body">
                      <span className="my-complaints-recipient-step">
                        {index + 1}
                      </span>
                      <div>
                        <span className="my-complaints-recipient-role">
                          {r.label}
                        </span>
                        <span className="my-complaints-recipient-name">
                          {r.name}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
            </div>
          </div>

          <div className="complaint-field">
            <label className="complaint-label">عنوان ال{kindLabel} *</label>
            <input
              type="text"
              className="complaint-input"
              placeholder={
                isInquiry
                  ? "مثال: استفسار عن سياسة الإجازات"
                  : "مثال: تأخر صرف الراتب"
              }
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="complaint-field">
            <label className="complaint-label">نص ال{kindLabel} *</label>
            <textarea
              className="complaint-textarea"
              rows={5}
              placeholder={
                isInquiry
                  ? "اكتب تفاصيل الاستفسار..."
                  : "اكتب تفاصيل الشكوى..."
              }
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <AttachmentUploader
            entityType="complaint"
            files={files}
            onFilesChange={setFiles}
          />

          {error && <p className="complaint-error">{error}</p>}

          <div className="my-complaints-form-buttons">
            <button
              type="button"
              className="complaint-secondary-btn"
              onClick={() => {
                setShowForm(false);
                setError("");
              }}
            >
              إلغاء
            </button>
            <button
              type="submit"
              className="complaint-submit-btn"
              disabled={recipientsLoading || recipients.length === 0}
            >
              إرسال ال{kindLabel}
            </button>
          </div>
        </form>
      )}

      {investigationSent && (
        <div className="complaint-form-card">
          <div className="complaint-success-box">
            <p className="complaint-success-title">تم إرسال طلب فتح التحقيق</p>
            <p className="complaint-success-subtitle">
              وصل الطلب للموارد البشرية، وتقدر تتابعه من صفحة تحقيقاتي.
            </p>
            <button
              className="complaint-secondary-btn"
              onClick={() => setInvestigationSent(false)}
            >
              إغلاق
            </button>
          </div>
        </div>
      )}

      {investigationTarget && (
        <form
          className="ess-table-card"
          onSubmit={async (event) => {
            event.preventDefault();
            const complaintId =
              investigationTarget._backendId || investigationTarget.id;
            setInvestigationSaving(true);
            setInvestigationError("");
            try {
              await requestInvestigation({
                complaintId,
                note: investigationNote.trim(),
              });
              setInvestigationTarget(null);
              setInvestigationNote("");
              setInvestigationSent(true);
            } catch (err) {
              const message = err?.response?.data?.message;
              setInvestigationError(
                Array.isArray(message)
                  ? message.join("\n")
                  : message || "تعذر إرسال طلب التحقيق",
              );
            } finally {
              setInvestigationSaving(false);
            }
          }}
        >
          <h3 className="my-complaints-form-title">
            طلب فتح تحقيق: {investigationTarget.title}
          </h3>
          <div className="complaint-field">
            <label className="complaint-label">ملاحظة للموارد البشرية (اختياري)</label>
            <textarea
              className="complaint-textarea"
              rows={3}
              value={investigationNote}
              onChange={(event) => setInvestigationNote(event.target.value)}
            />
          </div>
          {investigationError ? <p>{investigationError}</p> : null}
          <div className="my-complaints-form-buttons">
            <button
              type="button"
              className="complaint-secondary-btn"
              onClick={() => {
                setInvestigationTarget(null);
                setInvestigationError("");
              }}
            >
              إلغاء
            </button>
            <button
              type="submit"
              className="complaint-submit-btn"
              disabled={investigationSaving}
            >
              إرسال طلب التحقيق
            </button>
          </div>
        </form>
      )}

      {!showResign && (
        <div className="ess-table-card my-complaints-list">
          <h2 className="ess-table-title">
            قائمة الشكاوى والاستفسارات ({myComplaints.length})
          </h2>

          {myComplaints.length === 0 ? (
            <p className="ess-empty-message">
              لا توجد شكاوى أو استفسارات مقدمة حالياً
            </p>
          ) : (
            myComplaints.map((c) => (
              <ComplaintCard
                key={c.id}
                complaint={c}
                viewMode="sent"
                canRequestInvestigation={
                  (c.kind || COMPLAINT_KIND.COMPLAINT) ===
                  COMPLAINT_KIND.COMPLAINT
                }
                onRequestInvestigation={(complaint) => {
                  setInvestigationTarget(complaint);
                  setInvestigationNote("");
                  setInvestigationError("");
                  setSuccess(false);
                }}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}
